import type {
  AssistantActivity,
  AssistantSessionState,
  AssistantSettings,
  DraftAnalysis,
  ReviewResult,
  TicketDraft,
} from '@lixionary-jira/domain';
import { useQuery } from '@tanstack/react-query';
import { api } from './client';

export interface AssistantStatus {
  available: boolean;
  reviewOnCreate: boolean;
  model: string;
  reason?: string | null;
}

export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  activity: AssistantActivity[];
  createdAt: string;
}

export interface AssistantSession {
  id: string;
  displayId: string;
  status: AssistantSessionState;
  projectKey: string;
  messages: AssistantMessage[];
  activity: AssistantActivity[];
  draft: TicketDraft | null;
  analysis: DraftAnalysis | null;
  error: string | null;
  model: string | null;
  repositoryRevision: string | null;
  ticket: { key: string; number: number } | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssistantSessionSummary {
  id: string;
  displayId: string;
  status: AssistantSessionState;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface TicketAssistant {
  available: boolean;
  canRequestReview: boolean;
  canApply: boolean;
  ticketVersion: number;
  review: {
    id: string;
    displayId: string;
    status: AssistantSessionState;
    result: ReviewResult | null;
    activity: AssistantActivity[];
    error: string | null;
    model: string | null;
    repositoryRevision: string | null;
    scopeRevision: number | null;
    stale: boolean;
    createdAt: string;
    finishedAt: string | null;
  } | null;
  origin: {
    id: string;
    displayId: string;
    analysis: DraftAnalysis | null;
    model: string | null;
    repositoryRevision: string | null;
    createdAt: string;
  } | null;
}

export interface AdminAssistant {
  settings: AssistantSettings;
  available: boolean;
  reason: string | null;
  apiKeyConfigured: boolean;
  gitTokenConfigured: boolean;
  concurrency: number;
  updatedAt: string | null;
}

export const isBusy = (s: AssistantSessionState | undefined) => s === 'queued' || s === 'running';

/** Polls while the assistant works; the API has no push channel and long model runs outlive proxy timeouts. */
const POLL_MS = 1500;

export const useAssistantStatus = () =>
  useQuery({
    queryKey: ['assistant-status'],
    queryFn: () => api.get<AssistantStatus>('/assistant/status'),
    staleTime: 60_000,
  });

export const useAssistantSession = (projectKey: string | undefined, id: string | null) =>
  useQuery({
    queryKey: ['assistant-session', projectKey, id],
    queryFn: () => api.get<AssistantSession>(`/projects/${projectKey}/assistant/sessions/${id}`),
    enabled: !!projectKey && !!id,
    refetchInterval: (q) => (isBusy(q.state.data?.status) ? POLL_MS : false),
  });

export const useTicketAssistant = (projectKey: string, number: number) =>
  useQuery({
    queryKey: ['ticket-assistant', projectKey, number],
    queryFn: () => api.get<TicketAssistant>(`/projects/${projectKey}/tickets/${number}/assistant`),
    refetchInterval: (q) => (isBusy(q.state.data?.review?.status) ? POLL_MS * 2 : false),
  });
